import styles from './RequestsCard.module.css';

interface GaugeChartProps {
	value: number;
	max: number;
	label?: string;
}

interface RequestsCardProps {
	success: number;
	total: number;
	title?: string;
	subtitle?: string;
}

const CX = 150;
const CY = 150;
const R_MAIN = 120;
const R_TICKS = 105;
const STROKE_WIDTH = 12;
const PRIMARY_COLOR = 'var(--primary)';
const EMPTY_COLOR = 'var(--log-gauge-empty)';

const GaugeChart = ({ value, max, label }: GaugeChartProps) => {
	const rotation = 210;
	const startAngle = -205;
	const endAngle = 25;

	const polarToCartesian = (cx: number, cy: number, r: number, angleInDegrees: number) => {
		const angleInRadians = (angleInDegrees * Math.PI) / 180;
		return {
			x: cx + r * Math.cos(angleInRadians),
			y: cy + r * Math.sin(angleInRadians),
		};
	};

	const describeArc = (x: number, y: number, r: number, start: number, end: number) => {
		const startP = polarToCartesian(x, y, r, end);
		const endP = polarToCartesian(x, y, r, start);
		const largeArcFlag = end - start <= 180 ? '0' : '1';
		return `M ${startP.x} ${startP.y} A ${r} ${r} 0 ${largeArcFlag} 0 ${endP.x} ${endP.y}`;
	};

	const percentage = max > 0 ? Math.min(Math.max(value / max, 0), 1) : 0;
	const currentAngle = startAngle + percentage * rotation;

	const backgroundPath = describeArc(CX, CY, R_MAIN, startAngle, endAngle);
	const progressPath = describeArc(CX, CY, R_MAIN, startAngle, currentAngle);

	const tickElements = [];
	const totalTicks = 50;
	for (let i = 0; i <= totalTicks; i++) {
		const angle = startAngle + (i / totalTicks) * rotation;
		const p1 = polarToCartesian(CX, CY, R_TICKS, angle);
		const p2 = polarToCartesian(CX, CY, R_TICKS - 12, angle);
		tickElements.push(
			<line
				key={i}
				x1={p1.x}
				y1={p1.y}
				x2={p2.x}
				y2={p2.y}
				stroke={EMPTY_COLOR}
				strokeWidth="1.5"
				strokeLinecap="round"
			/>
		);
	}

	const [line1, line2] = label ? label.split('\n') : ['', ''];

	return (
		<svg viewBox="20 20 260 180" className={styles.gaugeSvg} preserveAspectRatio="xMidYMid meet">
			<defs>
				<radialGradient id="innerShadow" cx="50%" cy="100%" r="50%">
					<stop offset="0%" stopColor={PRIMARY_COLOR} stopOpacity="0.08" />
					<stop offset="100%" stopColor={PRIMARY_COLOR} stopOpacity="0" />
				</radialGradient>
			</defs>

			<path d={`${backgroundPath} L ${CX} ${CY} Z`} fill="url(#innerShadow)" stroke="none" />

			{tickElements}

			<path
				d={backgroundPath}
				fill="none"
				stroke={EMPTY_COLOR}
				strokeWidth={STROKE_WIDTH}
				strokeLinecap="round"
			/>

			<path
				d={progressPath}
				fill="none"
				stroke={PRIMARY_COLOR}
				strokeWidth={STROKE_WIDTH}
				strokeLinecap="round"
				style={{ transition: 'all 0.8s ease-out' }}
			/>

			<text x={CX} y={CY - 10} textAnchor="middle" fontWeight="700" className={styles.valueText}>
				{value.toLocaleString('pt-BR')}
			</text>
			<text x={CX} y={CY + 15} textAnchor="middle" className={styles.labelText}>
				{line1}
			</text>
			<text x={CX} y={CY + 32} textAnchor="middle" className={styles.labelText}>
				{line2}
			</text>
		</svg>
	);
};

export const RequestsCard = ({
	success,
	total,
	title = 'Requisições na Plataforma',
	subtitle = 'Todas as requisições feitas hoje no Practium',
}: RequestsCardProps) => {
	const label = `com sucesso de\n${total.toLocaleString('pt-BR')} requisições`;

	return (
		<div className={styles.card}>
			<div className={styles.header}>
				<p className={styles.title}>{title}</p>
				<p className={styles.subtitle}>{subtitle}</p>
			</div>
			<div className={styles.gaugeWrapper}>
				<GaugeChart value={success} max={total} label={label} />
			</div>
		</div>
	);
};
